import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApiErrorDetails } from 'app/shared/models/api-error';
import { ErrorDialogComponent, ErrorDialogData } from '../shared/ui/error-dialog/error-dialog';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private readonly dialog = inject(MatDialog);

  showError(error: ApiErrorDetails, customTitle?: string, customMessage?: string): void {
    const dialogData: ErrorDialogData = {
      title: customTitle || `Fehler ${error.statusCode}`,
      message: customMessage || error.message,
      details: error,
      showDetails: true,
    };

    this.openErrorDialog(dialogData);
  }

  handleHttpError(httpError: unknown, response: Response | undefined): void {
    if (this.isNetworkError(httpError, response)) {
      this.showNetworkError();
      return;
    }

    const errorData = this.parseErrorResponse(httpError);

    if (errorData && this.isForeignKeyConstraintError(errorData)) {
      this.showForeignKeyConstraintError(errorData);
      return;
    }

    if (errorData && this.isInsufficientPermissionsError(errorData)) {
      this.showInsufficientPermissionsError(errorData);
      return;
    }

    const apiError: ApiErrorDetails = {
      message: errorData?.message || `HTTP ${response?.status} Fehler`,
      statusCode: errorData?.status || response?.status || 500,
      detailsMessage: this.extractDetailsMessage(errorData?.details),
      path: response?.url,
    };

    this.showError(apiError);
  }

  private parseErrorResponse(error: unknown): {
    status?: number;
    message?: string;
    name?: string;
    details?: Record<string, unknown>;
  } | null {
    if (typeof error === 'object' && error !== null) {
      return error as {
        status?: number;
        message?: string;
        name?: string;
        details?: Record<string, unknown>;
      };
    }
    return null;
  }

  private extractDetailsMessage(details: Record<string, unknown> | undefined): string | undefined {
    if (!details) return undefined;

    return Object.entries(details)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(', ');
  }

  private isForeignKeyConstraintError(errorData: {
    status?: number;
    message?: string;
    details?: Record<string, unknown>;
  }): boolean {
    if (errorData.status !== 422 || errorData.message !== 'Database Error') {
      return false;
    }

    const detailsMessage = errorData.details?.['message'];
    if (typeof detailsMessage !== 'string') {
      return false;
    }

    return detailsMessage.includes('violates foreign key constraint');
  }

  private showForeignKeyConstraintError(errorData: {
    status?: number;
    message?: string;
    details?: Record<string, unknown>;
  }): void {
    const detailsMessage = errorData.details?.['message'] as string;
    const userMessage =
      'Dieser Datensatz kann nicht gelöscht werden, da er noch von anderen Datensätzen referenziert wird.';

    const dialogData: ErrorDialogData = {
      title: 'Datensatz wird noch verwendet',
      message: userMessage,
      details: {
        statusCode: 422,
        message: 'Database Constraint Violation',
        detailsMessage: detailsMessage,
        path: '',
      },
      showDetails: true,
    };

    this.openErrorDialog(dialogData);
  }

  private isInsufficientPermissionsError(errorData: {
    status?: number;
    message?: string;
    details?: Record<string, unknown>;
  }): boolean {
    return errorData.status === 403 && errorData.message === 'Insufficient permissions';
  }

  private showInsufficientPermissionsError(errorData: {
    status?: number;
    message?: string;
    details?: Record<string, unknown>;
  }): void {
    const requiredScopes = errorData.details?.['requiredScopes'] as string[] | undefined;
    const scopesList = requiredScopes?.join(', ') || 'Unbekannt';

    const dialogData: ErrorDialogData = {
      title: 'Fehlende Berechtigung',
      message: `Sie haben nicht die erforderlichen Berechtigungen für diese Aktion.\n\nErforderliche Berechtigungen: ${scopesList}`,
      showDetails: false,
    };

    this.openErrorDialog(dialogData);
  }

  private isNetworkError(error: unknown, response: Response | undefined): boolean {
    if (response !== undefined) {
      return false;
    }

    if (error instanceof TypeError) {
      return true;
    }

    if (error instanceof Error && error.name === 'TypeError') {
      return true;
    }

    return response === undefined;
  }

  private showNetworkError(): void {
    const dialogData: ErrorDialogData = {
      title: 'Verbindungsfehler',
      message:
        'Der Server ist nicht erreichbar. Bitte überprüfen Sie Ihre Internetverbindung oder versuchen Sie es später erneut.',
      showDetails: false,
    };

    this.openErrorDialog(dialogData);
  }

  private openErrorDialog(dialogData: ErrorDialogData) {
    this.dialog.open(ErrorDialogComponent, {
      data: dialogData,
      width: '500px',
      maxWidth: '90vw',
      disableClose: false,
    });
  }
}
